import { format } from 'date-fns';
import { DateTime } from 'luxon';

import { CalculatedDelivery } from 'models/shipments';

export function localDateTimeFromISO(isoDateStr: string) {
  const dateTime = DateTime.fromISO(isoDateStr, { locale: 'en-US', setZone: true });
  const local = DateTime.local();
  return dateTime.setZone(local.zone, { keepLocalTime: true });
}

export function localJSDateFromISO(isoDateStr: string) {
  return localDateTimeFromISO(isoDateStr).toJSDate();
}

export function formatJSDate(date: Date, format: string) {
  return DateTime.fromJSDate(date).toFormat(format);
}

/**
 * Takes an ISO string and returns the date and time at that locale
 * @param isoDateStr - ISO 8601 date string
 * @returns A string in the format `EEE d MMM, h.mma`
 * @example
 * getEventDateTimeFromISO('2017-08-07T07:37:50+9:00')
 * returns '07:37am Mon 07 Aug' regardless of your system timezone
 */
export function getEventDateTimeFromISO(isoDateStr: string): { date: string; time: string } | null {
  const eventDateTime: DateTime = DateTime.fromISO(isoDateStr, { setZone: true });
  if (!eventDateTime.isValid) return null;
  const dateStr = eventDateTime.toFormat('EEE d MMM');
  const timeStr = eventDateTime.toFormat('h.mma').toLowerCase();
  return {
    date: dateStr,
    time: timeStr,
  };
}

export function getCalculatedDeliveryDateText(estimateDateObj: CalculatedDelivery | null) {
  const { expectedBetween, onDate } = estimateDateObj || {};
  if (!expectedBetween && !onDate) return;
  const eddRangeText = getEddRangeText(expectedBetween?.fromDate, expectedBetween?.toDate);
  if (eddRangeText) return eddRangeText;
  const eddSingleText = onDate && getEddText(onDate);
  if (eddSingleText) return eddSingleText;
}

export function getEddText(date: string) {
  const deliveryDateTime: DateTime = DateTime.fromISO(date, { setZone: true });
  return deliveryDateTime.toFormat('EEEE d MMMM');
}

export function getEddRangeText(fromDateStr?: string, toDateStr?: string) {
  if (!fromDateStr || !toDateStr) return '';
  const fromDate = localDateTimeFromISO(fromDateStr);
  const toDate = localDateTimeFromISO(toDateStr);
  if (!fromDate.isValid || !toDate.isValid) return '';
  const isSameMonth = fromDate.month === toDate.month;
  const startDate = isSameMonth ? fromDate.toFormat('EEE d') : fromDate.toFormat('EEE d MMM');
  const endDate = toDate.toFormat('EEE d MMM');
  return `${startDate} – ${endDate}`;
}

/**
 * Formats a time window string
 * @param time time string in the format of hhmmss (eg: 131000)
 * @param pattern a pattern to format to. Defaults to `h.mmaaa`
 * @returns A string formatted with the given pattern
 */
export function formatTimeWindow(time: string, pattern = 'h.mmaaa') {
  if (time.length != 6) return '';
  const hour = parseInt(time.substring(0, 2), 10);
  if (!hour) return '';
  const minute = parseInt(time.substring(2, 4), 10) || 0;
  const date = new Date();
  date.setHours(hour, minute);
  return format(date, pattern);
}

export function isToday(dateString: string): boolean {
  const todayDate = DateTime.now().toISODate();
  return todayDate === dateString;
}
